import { PosHit } from '../api';

/** Nova Scotia HST. Matches `TAX_RATE` in backroom-server's checkout.rs
 *  and the webapp's utils/tax.ts — keep all three in step. */
export const TAX_RATE = 0.14;

/** One line on the till. `hit` is the catalog row as fetched; `qty` is
 *  what the cashier rang up. Scanning the same item twice bumps `qty`
 *  rather than adding a second line. */
export type CartLine = {
  hit: PosHit;
  qty: number;
};

/** Pseudo-tenant for custom-amount lines. Never a real tenant slug, so
 *  nothing that keys off `tenant` can confuse one with a catalog item. */
export const CUSTOM_TENANT = 'custom';

/** Fallback name when the cashier doesn't type one. */
export const CUSTOM_DEFAULT_NAME = 'Custom item';

export function isCustom(hit: PosHit): boolean {
  return hit.tenant === CUSTOM_TENANT;
}

/** Monotonic within a session — enough to keep two custom lines
 *  distinct, which is all `lineKey` needs. Deliberately not stable
 *  across reloads; nothing persists a cart. */
let customSeq = 0;

/** Build a synthetic `PosHit` for a custom-amount line.
 *
 *  Modelling these as a `PosHit` rather than a separate cart-line
 *  variant means totals, quantity, employee pricing, and `lineKey` all
 *  work unchanged — the only thing that knows the difference is the
 *  rendering. `cost` is null: a manually-priced item has no cost basis,
 *  so employee mode falls back to the entered price and flags the line,
 *  which is the honest behaviour. */
export function customHit(name: string, priceCents: number): PosHit {
  customSeq += 1;
  const trimmed = name.trim();
  return {
    id: `custom-${customSeq}`,
    tenant: CUSTOM_TENANT,
    tenant_label: 'Custom',
    name: trimmed === '' ? CUSTOM_DEFAULT_NAME : trimmed,
    price: priceCents,
    cost: null,
    sku: '',
    stock: 0,
    // A manually-entered amount is taxed: the cashier reaching for
    // this button is ringing up something ordinary that isn't in the
    // catalog, not claiming an exemption.
    tax_exempt: false,
  };
}

/** Cart identity for a hit.
 *
 *  Item ids are unique only *within* a tenant, so `id` alone is not a
 *  safe key across the three catalogs the register sells from — two
 *  shops can hold the same id and would collide into one line, silently
 *  merging two different products. */
export function lineKey(hit: PosHit): string {
  return `${hit.tenant}:${hit.id}`;
}

/** Money mode for the whole sale.
 *
 *  `employee` and `owner` both price at cost, and both are taxed. They
 *  differ in whether the money counts: an employee purchase is a real
 *  sale that belongs in the day's takings, because the stock had
 *  reached the shelf and was sold from it. An **owner withdrawal is not
 *  a sale** — no money changes hands and it never appears as revenue —
 *  but HST is still recorded on the cost, because taking stock for
 *  personal use is a self-supply.
 *
 *  The revenue asymmetry is a tax rule, not a courtesy. Selling below
 *  margin to a customer or an employee is allowed; "selling" to
 *  yourself at a loss would reclaim HST on your own margin, so for
 *  revenue purposes the goods are treated as having left before they
 *  were ever on sale. */
export type PriceBasis = 'retail' | 'employee' | 'owner';

/** True when the basis takes money from a customer. Owner withdrawals
 *  are the one basis that doesn't. */
export function isRevenueBasis(basis: PriceBasis): boolean {
  return basis !== 'owner';
}

/** True when the basis prices at cost rather than retail. */
export function isCostBasis(basis: PriceBasis): boolean {
  return basis === 'employee' || basis === 'owner';
}

/** Cents → "$1,234.56". Negative values keep the sign outside the
 *  dollar sign ("-$4.00") because that reads faster on a till than
 *  "$-4.00". */
export function formatCents(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  const dollarStr = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const centStr = remainder.toString().padStart(2, '0');
  return `${negative ? '-' : ''}$${dollarStr}.${centStr}`;
}

/** True when an employee-priced line has no `cost` on the item and is
 *  therefore falling back to retail. The confirm dialog names these
 *  explicitly so nobody assumes they were discounted. */
export function isMissingCost(line: CartLine, basis: PriceBasis): boolean {
  return isCostBasis(basis) && line.hit.cost == null;
}

/** Unit price for a line under the given basis, integer cents.
 *
 *  The cost bases (employee, owner) use `cost`. When an item has no cost recorded we
 *  fall back to `price` rather than selling it for $0 — a missing cost
 *  is a catalog gap, not a giveaway. `isMissingCost` surfaces that to
 *  the cashier. */
export function unitPrice(line: CartLine, basis: PriceBasis): number {
  const retail = line.hit.price ?? 0;
  if (!isCostBasis(basis)) return retail;
  return line.hit.cost ?? retail;
}

export function lineTotal(line: CartLine, basis: PriceBasis): number {
  return unitPrice(line, basis) * line.qty;
}

export type Totals = {
  subtotal: number;
  tax: number;
  total: number;
};

/** Sale totals, integer cents throughout.
 *
 *  Tax is applied once to the summed *taxable* subtotal, not per line —
 *  rounding each line separately drifts by a cent or two on a big
 *  basket and the drawer never reconciles. Tax-exempt lines are in the
 *  subtotal but not in the taxable base, which is what lets a mixed
 *  basket ring up correctly. */
export function computeTotals(lines: CartLine[], basis: PriceBasis): Totals {
  const subtotal = lines.reduce((sum, l) => sum + lineTotal(l, basis), 0);
  // Every basis is taxed, owner withdrawals included: taking stock for
  // personal use is a self-supply, so the HST claimed on the way in is
  // accounted for on the way out. Only the per-item exemption flag
  // zeroes a line.
  const taxable = lines.reduce(
    (sum, l) => (l.hit.tax_exempt ? sum : sum + lineTotal(l, basis)),
    0,
  );
  const tax = Math.round(taxable * TAX_RATE);
  return { subtotal, tax, total: subtotal + tax };
}

/** What the customer would have paid at retail, for the "saved vs
 *  retail" line and the scary confirm dialog. Always computed on the
 *  retail basis regardless of the current mode. */
export function retailTotals(lines: CartLine[]): Totals {
  return computeTotals(lines, 'retail');
}

/** One line staged for refund. `quantity` is bounded by the line's
 *  `refundable_quantity`; `unitPrice` is what was actually charged on
 *  the original sale, never the current catalog price. */
export type RefundSelection = {
  lineIndex: number;
  name: string;
  unitPrice: number;
  quantity: number;
  maxQuantity: number;
  /** Whether HST was charged on this line at the time of sale, read
   *  off the stored sale rather than the current catalog — so a refund
   *  gives back what was taken, even if the item's flag has flipped
   *  since. */
  taxExempt: boolean;
  restock: boolean;
  /** False when the line has no catalog id — nothing to restock
   *  against, so the toggle is forced off and disabled. */
  restockable: boolean;
};

/** Refund totals, integer cents.
 *
 *  Tax is recomputed on the refunded subtotal rather than read from the
 *  sale: per-line tax is NULL on every stored row. Exempt lines are
 *  excluded from the taxable base, using the same per-line flag the
 *  server resolves — the cashier counts cash out of this preview, so it
 *  must land on the server's number, not near it. Rounded once,
 *  matching `computeTotals`. The server still recomputes from stored
 *  prices and its answer remains authoritative. */
export function refundTotals(selections: RefundSelection[]): Totals {
  // Owner withdrawals included: returning one reverses the cost and the
  // HST that were recorded when it was taken, so the figures are the
  // same ones. What differs is that no cash moves — see the tender.
  const subtotal = selections.reduce(
    (sum, s) => sum + Math.round(s.unitPrice * s.quantity),
    0,
  );
  const taxable = selections.reduce(
    (sum, s) => (s.taxExempt ? sum : sum + Math.round(s.unitPrice * s.quantity)),
    0,
  );
  const tax = Math.round(taxable * TAX_RATE);
  return { subtotal, tax, total: subtotal + tax };
}

/** Suggested cash-tender buttons for a total, in cents, ascending and
 *  de-duplicated: exact, next $5 / $10 / $20, and any of the common
 *  bills ($20/$50/$100) that exceed the total. Caps at four so the row
 *  doesn't wrap on a narrow till screen. */
export function quickTenders(total: number): number[] {
  if (total <= 0) return [];
  const roundUpTo = (step: number) => Math.ceil(total / step) * step;
  const candidates = [
    total,
    roundUpTo(500),
    roundUpTo(1000),
    roundUpTo(2000),
    2000,
    5000,
    10000,
  ];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const c of candidates) {
    if (c < total || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out.sort((a, b) => a - b).slice(0, 4);
}
